import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Layout from '../Components/Layout';
import PaginatedItems from "../Components/Pagination";

const Vendors = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [sortBy, setSortBy] = useState('id');
    const [sortOrder, setSortOrder] = useState('desc');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);
    const searchTimerRef = useRef(null);

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearch(value);
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(() => {
            setDebouncedSearch(value);
        }, 300);
    };

    useEffect(() => {
        return () => {
            if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        };
    }, []);

    const fetchData = async (page = 1) => {
        setLoading(true);
        try {
            const params = {
                page: page.toString(),
                limit: '10',
                sort_by: sortBy,
                sort_order: sortOrder
            };
            
            if (search) params.search = search;
            
            const response = await axios.get('/library/vendors', { params });
            const result = response.data;
            
            if (result.success) {
                setData(result.data);
                setCurrentPage(result.pagination.current_page);
                setTotalPages(result.pagination.total_pages);
                setTotalRecords(result.pagination.total_records);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(1);
    }, [debouncedSearch, sortBy, sortOrder]);

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('asc');
        }
    };

    const handlePageChange = (page) => {
        fetchData(page);
    };

    const paginatePage = (page) => {
        console.log(`pageNumber ! ${page}`)
        handlePageChange(page);
    };

    return (
        <Layout>
            <div style={{ height: 'calc(100vh - 70px)', overflow: 'auto', padding: '20px 0px' }}>
                <div className="container-fluid vendors">
            {/* Search and Actions Bar */}
            <div className="row mb-3 p-3 bg-light">
                <div className="col-md-6">
                    <div className="input-group">
                        <span className="input-group-text">
                            <i className="ti-search"></i>
                        </span>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search vendors, companies, contacts..."
                            value={search}
                            onChange={handleSearchChange}
                        />
                    </div>
                </div>
                <div className="col-md-6 text-end">
                    <small className="text-muted">
                        {totalRecords} vendors found
                    </small>
                </div>
            </div>

            {/* Data Table with Horizontal Scroll */}
            <div className="table-responsive" style={{ overflowX: 'auto' }}>
                {loading ? (
                    <div className="text-center p-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden"></span>
                        </div>
                        <p className="mt-2">Loading vendors...</p>
                    </div>
                ) : (
                    <table className="table table-hover" style={{ minWidth: '1500px' }}>
                        <thead className="table-light">
                            <tr>
                                <th 
                                    onClick={() => handleSort('id')}
                                    style={{ cursor: 'pointer', minWidth: '60px' }}
                                >
                                    ID {sortBy === 'id' && (
                                        <i className={`ti-arrow-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
                                    )}
                                </th>
                                <th 
                                    onClick={() => handleSort('ActiveStatus')}
                                    style={{ cursor: 'pointer', minWidth: '100px' }}
                                >
                                    Status {sortBy === 'ActiveStatus' && (
                                        <i className={`ti-arrow-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
                                    )}
                                </th>
                                <th 
                                    onClick={() => handleSort('Vendor')}
                                    style={{ cursor: 'pointer', minWidth: '150px' }}
                                >
                                    Vendor {sortBy === 'Vendor' && (
                                        <i className={`ti-arrow-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
                                    )}
                                </th>
                                <th 
                                    onClick={() => handleSort('Company')}
                                    style={{ cursor: 'pointer', minWidth: '150px' }}
                                >
                                    Company {sortBy === 'Company' && (
                                        <i className={`ti-arrow-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
                                    )}
                                </th>
                                <th 
                                    onClick={() => handleSort('PrimaryContact')}
                                    style={{ cursor: 'pointer', minWidth: '150px' }}
                                >
                                    Primary Contact {sortBy === 'PrimaryContact' && (
                                        <i className={`ti-arrow-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
                                    )}
                                </th>
                                <th 
                                    onClick={() => handleSort('MainPhone')}
                                    style={{ cursor: 'pointer', minWidth: '130px' }}
                                >
                                    Main Phone {sortBy === 'MainPhone' && (
                                        <i className={`ti-arrow-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
                                    )}
                                </th>
                                <th style={{ minWidth: '130px' }}>Work Phone</th>
                                <th style={{ minWidth: '130px' }}>Mobile</th>
                                <th style={{ minWidth: '130px' }}>Fax</th>
                                <th 
                                    onClick={() => handleSort('MainEmail')}
                                    style={{ cursor: 'pointer', minWidth: '200px' }}
                                >
                                    Main Email {sortBy === 'MainEmail' && (
                                        <i className={`ti-arrow-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
                                    )}
                                </th>
                                <th style={{ minWidth: '150px' }}>Other 1</th>
                                <th style={{ minWidth: '200px' }}>Bill From Street</th>
                                <th style={{ minWidth: '100px' }}>Bill From Zip</th>
                                <th style={{ minWidth: '100px' }}>Ship From Zip</th>
                                <th 
                                    onClick={() => handleSort('Terms')}
                                    style={{ cursor: 'pointer', minWidth: '100px' }}
                                >
                                    Terms {sortBy === 'Terms' && (
                                        <i className={`ti-arrow-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
                                    )}
                                </th>
                                <th 
                                    onClick={() => handleSort('CreditLimit')}
                                    style={{ cursor: 'pointer', minWidth: '120px' }}
                                >
                                    Credit Limit {sortBy === 'CreditLimit' && (
                                        <i className={`ti-arrow-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
                                    )}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((vendor) => (
                                <tr key={vendor.id}>
                                    <td>{vendor.id}</td>
                                    <td>{vendor.ActiveStatus}</td>
                                    <td>{vendor.Vendor}</td>
                                    <td>{vendor.Company}</td>
                                    <td>{vendor.PrimaryContact}</td>
                                    <td>{vendor.MainPhone}</td>
                                    <td>{vendor.WorkPhone}</td>
                                    <td>{vendor.Mobile}</td>
                                    <td>{vendor.Fax}</td>
                                    <td>{vendor.MainEmail}</td>
                                    <td>{vendor.Other1}</td>
                                    <td>{vendor.BillFromStreet1}</td>
                                    <td>{vendor.BillFromZip}</td>
                                    <td>{vendor.ShipFromZip}</td>
                                    <td>{vendor.Terms}</td>
                                    <td>{vendor.CreditLimit}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Pagination - Using PaginatedItems Component */}
            {!loading && totalRecords > 0 && (
                <div className="col-lg-12 mb-12" style={{marginTop:'20px'}}>
                    <PaginatedItems total={totalRecords} onPageChange={paginatePage} currentPage={currentPage}></PaginatedItems>
                </div>
            )}
                </div>
            </div>
        </Layout>
    );
};

export default Vendors;